/** The server functions tRPC cannot carry, such as streaming; everything else goes through `@/lib/trpc`. */

// MARK: Imports

import { createServerFn } from '@tanstack/react-start';
import { isServer } from '@tanstack/react-query';
import { currentBuildAssets, reportIsUnattributable, stackBundleIsCurrent } from './build-assets';
import { parseRequest } from './server-functions-shared.server';
import { eventEmitter } from './server-broadcast.server';
import { clientTelemetryContext, telemetryCaller } from './wide-event.server';
import { allowTelemetryActions, allowTelemetryErrorReport } from './telemetry-rate-limit.server';
import {
	type ClientAction,
	type ClientErrorBreadcrumb,
	PORTAL_FRONTEND_ERROR_MARKER,
	recordClientActions,
	recordClientError,
	reportEventError,
	reportRawError,
} from '@worldcoo/monolith/wide-events';
/** From the leaf module, or the barrel and its `node:crypto` importers ship to the browser. docs/notes/portals/data.md */
import { CLIENT_ERROR_SOURCES } from '@worldcoo/monolith/wide-events/client-error-sources';
import * as z from 'zod';
import { shouldReportFrontendError } from './frontend-error-filter';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';

// MARK: Types

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';
export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';
export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';
export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';
export type LiveCounterMode = 'day' | 'realtime';

// MARK: Shared helpers

export async function ensureSeoData(context: { isBot: boolean }, promise: Promise<unknown>): Promise<void> {
	if (isServer && context.isBot) {
		await promise;
	}
}

// Lives in `./live-window` so the tRPC server modules need not import this file; the www routes import it here.
export { currentWindowKey } from './live-window';

// MARK: Server functions called directly

export const getRequestContext = createServerFn()
	.validator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

/** Truncating, not `.max()`-rejecting: one oversized crumb from a stale bundle must not void its report. */
const breadcrumbSchema = z.object({
	t: z.number(),
	type: z.string().transform((value) => value.slice(0, 32)),
	message: z.string().transform((value) => value.slice(0, 200)),
}) satisfies z.ZodType<ClientErrorBreadcrumb>;

const clientActionSchema = z.object({
	t: z.number(),
	type: z.enum(['nav', 'click']),
	label: z.string().max(120),
	path: z.string().max(300),
}) satisfies z.ZodType<ClientAction>;

/**
 * Bounded again because the endpoint is open to any POST and lands in a stderr line CloudWatch cuts at 256KB.
 * `catch` and truncation both keep one malformed field from failing the whole report.
 */
const boundedErrorText = (max: number, fallback: string) =>
	z
		.string()
		.catch(fallback)
		.transform((value) => value.slice(0, max));

/** No `.catch()`: a fallback would turn "not sent" into a placeholder string. */
const boundedOptionalErrorText = (max: number) =>
	z
		.string()
		.transform((value) => value.slice(0, max))
		.optional();

/**
 * Shared by the big optional fields, because `reportEventError` truncates the event at `MAX_REPORT_LEN` into
 * invalid JSON. What is dropped first, and why 11_000: docs/notes/portals/data.md
 */
const CLIENT_FIELDS_BUDGET = 11_000;
const MAX_STACK_LEN = 9_000;
const MAX_CAUSE_LEN = 3_000;
const MAX_COUNTERS_LEN = 600;
const MAX_SCRIPT_LEN = 500;
const MAX_MISMATCH_LEN = 400;

/** Measured serialized, so escape inflation cannot cheat the budget. */
function fitSerialized(value: string, budget: number): string {
	let sliced = value.slice(0, Math.max(0, budget));
	let serialized = JSON.stringify(sliced).length;
	while (serialized > budget && sliced.length > 0) {
		// By the inflation ratio: a stack serializes at ~2x, and cutting the raw excess overshoots to nothing.
		sliced = sliced.slice(0, Math.floor((budget * sliced.length) / serialized));
		serialized = JSON.stringify(sliced).length;
	}
	return sliced;
}

export const logErrorInputSchema = z
	.object({
		route: boundedErrorText(300, 'unknown'),
		name: boundedErrorText(200, 'UnknownError'),
		message: boundedErrorText(2000, '(no message)'),
		stack: boundedOptionalErrorText(MAX_STACK_LEN),
		// Tighter than `stack`: pure addition to a line that has to fit.
		cause: boundedOptionalErrorText(MAX_CAUSE_LEN),
		// Not a breadcrumb: as a 21st one it tripped the array bound and voided the richest reports.
		counters: boundedOptionalErrorText(MAX_COUNTERS_LEN),
		// The only locator on a stackless report, so it is charged before `stack`.
		script: boundedOptionalErrorText(MAX_SCRIPT_LEN),
		// Not in `counters`, whose bound the counter snapshot already contests.
		mismatch: boundedOptionalErrorText(MAX_MISMATCH_LEN),
		// From the event's own list: an unlisted value fails validation and loses the whole report.
		source: z.enum(CLIENT_ERROR_SOURCES).optional(),
		// Client-asserted, so trusted only to skip a ticket the `client_error` event still records.
		documentReplayed: z.boolean().optional(),
		// Keeps the newest: the actions just before the crash explain it.
		breadcrumbs: z
			.array(breadcrumbSchema)
			.transform((all) => all.slice(-20))
			.optional(),
	})
	// In priority order; see CLIENT_FIELDS_BUDGET.
	.transform((data) => {
		let remaining = CLIENT_FIELDS_BUDGET;
		const spend = (value: string | undefined, max: number): string | undefined => {
			if (!value) return undefined;
			const fitted = fitSerialized(value, Math.min(max, remaining));
			if (!fitted) return undefined;
			remaining -= JSON.stringify(fitted).length;
			return fitted;
		};
		const script = spend(data.script, MAX_SCRIPT_LEN);
		const mismatch = spend(data.mismatch, MAX_MISMATCH_LEN);
		const stack = spend(data.stack, MAX_STACK_LEN);
		const counters = spend(data.counters, MAX_COUNTERS_LEN);
		const cause = spend(data.cause, MAX_CAUSE_LEN);
		const breadcrumbs = [...(data.breadcrumbs ?? [])];
		while (breadcrumbs.length > 0 && JSON.stringify(breadcrumbs).length > remaining) breadcrumbs.shift();
		return {
			...data,
			script,
			mismatch,
			stack,
			counters,
			cause,
			breadcrumbs: breadcrumbs.length > 0 ? breadcrumbs : undefined,
		};
	});

export const logErrorFn = createServerFn({ method: 'POST' })
	.validator(logErrorInputSchema)
	.handler(({ data }) => {
		// The caller controls the fingerprint, so an unbounded open endpoint mints Jira issues. Silent, because a
		// failing call is one more console error; before the cookie and HMAC work a hammering script would cost.
		if (!allowTelemetryErrorReport(telemetryCaller())) {
			return { success: true, logged: false, ticketed: false };
		}

		// The ticket prints this same event, so both describe the crash identically. docs/notes/portals/data.md
		const bundleCurrent = stackBundleIsCurrent(data.stack, currentBuildAssets());
		const event = recordClientError({ ...clientTelemetryContext(), ...data, bundleCurrent });

		// A long-lived tab keeps an old filter across deploys (TEC-3648); the analytics row still counts it.
		if (!shouldReportFrontendError(data.name, data.message, data.stack, data.cause)) {
			return { success: true, logged: true, ticketed: false };
		}

		// Crawlers replaying one document across facet URLs (TEC-3854): not fixable here, still in analytics.
		if (data.documentReplayed) {
			return { success: true, logged: true, ticketed: false };
		}

		// A replayed document runs the old bundle, which cannot send `documentReplayed` (355 of 358 pro #418s in 14 days).
		if (reportIsUnattributable({ ...data, bundleCurrent })) {
			return { success: true, logged: true, ticketed: false };
		}

		// One line may carry the marker, since the parser tickets each occurrence; `report-line.ts` owns that rule.
		if (event) {
			reportEventError(event, { marker: PORTAL_FRONTEND_ERROR_MARKER });
		} else {
			reportRawError(PORTAL_FRONTEND_ERROR_MARKER, `${data.name} — ${data.message}`);
		}
		return { success: true, logged: true, ticketed: true };
	});

/**
 * Bounded per call and per caller, since each action is a queue message and a durable row; excess is dropped
 * silently so a failing call cannot pollute the signal. What the client may collect: `lib/session-actions.ts`.
 */
export const recordClientActionsFn = createServerFn({ method: 'POST' })
	.validator(z.object({ actions: z.array(clientActionSchema).max(50) }))
	.handler(({ data }) => {
		const allowed = allowTelemetryActions(telemetryCaller(), data.actions.length);
		if (allowed > 0) {
			recordClientActions({
				...clientTelemetryContext(),
				actions: data.actions.slice(0, allowed),
				nowMs: Date.now(),
			});
		}
		return { success: true } as const;
	});

// MARK: Streaming server functions

export const waitForConfirmedDonationFn = createServerFn()
	.validator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};
			eventEmitter.on('donation.confirmed', onConfirmed);
			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});
		yield await promise;
	});

// MARK: Utility functions

/** Covers Adyen's wrapper around a DOMException, and stops a cyclic chain. */
const MAX_CAUSE_DEPTH = 3;

/**
 * Without it an `AdyenCheckoutError` ticket says only "something went wrong". Its own field, because `message`
 * and `stack` feed `computeFingerprint` and grafted frames would read as foreign.
 */
function describeCauseChain(error: unknown): string | undefined {
	const parts: string[] = [];
	try {
		let current: unknown = error;
		for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
			if (!(current instanceof Error)) break;
			const cause: unknown = current.cause;
			if (cause === undefined || cause === null) break;
			if (cause instanceof Error) {
				parts.push(`Caused by: ${cause.name}: ${cause.message}\n${cause.stack ?? '(no stack)'}`);
			} else {
				parts.push(`Caused by: ${typeof cause === 'string' ? cause : safeStringify(cause)}`);
				break;
			}
			current = cause;
		}
	} catch {
		// A failure to enrich a report must never lose the report.
	}
	return parts.length === 0 ? undefined : parts.join('\n\n');
}

export interface ExtractedErrorInfo {
	name: string;
	message: string;
	stack: string | undefined;
	cause?: string;
}

export function extractErrorInfo(error: unknown): ExtractedErrorInfo {
	if (error instanceof Error) {
		return {
			name: error.name || 'Error',
			message: error.message || '(empty message)',
			stack: error.stack,
			cause: describeCauseChain(error),
		};
	}
	if (typeof error === 'string') {
		return { name: 'StringError', message: error, stack: undefined };
	}
	if (error === null || error === undefined) {
		return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined };
	}
	if (typeof error === 'object') {
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError';
		const message =
			'message' in error && typeof error.message === 'string'
				? error.message
				: 'error' in error && typeof error.error === 'string'
					? error.error
					: safeStringify(error);
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message, stack };
	}
	return { name: 'UnknownError', message: safeStringify(error), stack: undefined };
}

/** One builder, so a new field is not a ten-site edit where a missed site silently reports less. */
export function errorReportData(
	namePrefix: string,
	info: ExtractedErrorInfo,
	extra?: Partial<z.input<typeof logErrorInputSchema>>,
): z.input<typeof logErrorInputSchema> {
	const { name, ...rest } = info;
	return { route: window.location.pathname, name: namePrefix + name, ...rest, ...extra };
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

// The reporting predicates live in the dependency-free ./frontend-error-filter; import them from there.
